import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  CreditCard, Wallet, Shield, CheckCircle, AlertCircle, ExternalLink, 
  Loader2, ArrowRight, DollarSign, RefreshCw, Building
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSettingsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading, isAuthenticated, token } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboarding, setOnboarding] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }
    if (isAuthenticated) {
      fetchSettings();
      
      // Check for Connect return/refresh params
      const connectParam = searchParams.get('connect');
      if (connectParam === 'return') {
        toast.success('Stripe Connect setup updated');
        fetchSettings();
      } else if (connectParam === 'refresh') {
        handleRefreshOnboarding();
      }
    }
  }, [isAuthenticated, authLoading, searchParams]);

  const getAuthHeaders = () => ({
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    withCredentials: true
  });

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/payments/settings`, getAuthHeaders());
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load payment settings');
    } finally {
      setLoading(false);
    }
  };

  const handleStartOnboarding = async () => {
    setOnboarding(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API}/payments/connect/onboard`,
        { origin_url: originUrl },
        getAuthHeaders()
      );
      
      if (response.data.onboarding_url) {
        window.location.href = response.data.onboarding_url;
      } else if (response.data.status === 'active') {
        toast.success('Your Connect account is already active!');
        fetchSettings();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start onboarding');
    } finally {
      setOnboarding(false);
    }
  };

  const handleRefreshOnboarding = async () => {
    setOnboarding(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API}/payments/connect/onboard`,
        { origin_url: originUrl },
        getAuthHeaders()
      );
      
      if (response.data.onboarding_url) {
        window.location.href = response.data.onboarding_url;
      }
    } catch (error) {
      toast.error('Failed to refresh onboarding');
    } finally {
      setOnboarding(false);
    }
  };

  const handleOpenDashboard = async () => {
    try {
      const response = await axios.get(`${API}/payments/connect/dashboard`, getAuthHeaders());
      if (response.data.url) {
        window.open(response.data.url, '_blank');
      }
    } catch (error) {
      toast.error('Failed to open Stripe dashboard');
    }
  };

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="h-96 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-trust-blue" />
        </div>
      </Layout>
    );
  }

  const isOwner = user?.role === 'owner';

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="payment-settings-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-slate-900">
            Payment Settings
          </h1>
          <p className="text-slate-600 mt-2">
            Manage your payment preferences and payout settings
          </p>
        </div>

        {/* Payment Status Card */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                settings?.payments_enabled ? 'bg-green-100' : 'bg-amber-100'
              }`}>
                {settings?.payments_enabled ? (
                  <CheckCircle className="w-6 h-6 text-green-600" />
                ) : (
                  <AlertCircle className="w-6 h-6 text-amber-600" />
                )}
              </div>
              <div>
                <h3 className="font-heading font-semibold text-slate-900">
                  Payment Status
                </h3>
                <p className="text-sm text-slate-500">
                  {settings?.payments_enabled 
                    ? 'Payments are enabled on your account'
                    : 'Complete setup to enable payments'
                  }
                </p>
              </div>
            </div>
            <Badge 
              variant={settings?.payments_enabled ? 'default' : 'secondary'}
              className={settings?.payments_enabled ? 'bg-green-600' : ''}
            >
              {settings?.payments_enabled ? 'Active' : 'Setup Required'}
            </Badge>
          </div>
        </div>

        {/* Platform Fee Info (Read-only) */}
        <div className="bg-soft-gray rounded-2xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <DollarSign className="w-5 h-5 text-trust-blue" />
            <h3 className="font-heading font-semibold text-slate-900">
              Platform Fee
            </h3>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-slate-600">
              Stay Pair Homes charges a small platform fee on each booking to maintain 
              the service and provide secure payment processing.
            </p>
            <div className="text-right shrink-0 ml-4">
              <span className="font-heading text-2xl font-bold text-trust-blue">
                {settings?.platform_fee_percent || 10}%
              </span>
              <p className="text-xs text-slate-500">of rent amount</p>
            </div>
          </div>
        </div>

        {/* Owner-specific: Stripe Connect */}
        {isOwner && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <Wallet className="w-5 h-5 text-trust-blue" />
              <h3 className="font-heading font-semibold text-slate-900">
                Payout Account (Stripe Connect)
              </h3>
            </div>
            
            {settings?.connect_status === 'not_created' ? (
              <div>
                <p className="text-slate-600 mb-4">
                  Connect your bank account to receive payouts when renters pay for bookings.
                  We use Stripe to securely handle all payments.
                </p>
                <div className="bg-soft-gray rounded-xl p-4 mb-4">
                  <h4 className="font-medium text-slate-900 mb-2">What you'll need:</h4>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• Valid email address</li>
                    <li>• Business or personal information</li>
                    <li>• Bank account for payouts</li>
                    <li>• Social Security Number (for tax purposes)</li>
                  </ul>
                </div>
                <Button
                  onClick={handleStartOnboarding}
                  disabled={onboarding}
                  className="bg-trust-blue hover:bg-trust-blue-hover text-white rounded-full"
                  data-testid="start-connect-onboarding"
                >
                  {onboarding ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Setting up...
                    </>
                  ) : (
                    <>
                      Set Up Payouts
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            ) : settings?.connect_status === 'pending' ? (
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <AlertCircle className="w-5 h-5 text-amber-500" />
                  <p className="text-slate-600">
                    Your account setup is incomplete. Please complete the onboarding process.
                  </p>
                </div>
                <Progress value={50} className="mb-4" />
                <div className="flex gap-3">
                  <Button
                    onClick={handleRefreshOnboarding}
                    disabled={onboarding}
                    className="bg-trust-blue hover:bg-trust-blue-hover text-white rounded-full"
                  >
                    {onboarding ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    Continue Setup
                  </Button>
                  <Button
                    variant="outline"
                    onClick={fetchSettings}
                    className="rounded-full"
                  >
                    Check Status
                  </Button>
                </div>
              </div>
            ) : settings?.connect_status === 'active' ? (
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <div>
                    <p className="text-slate-900 font-medium">
                      Payout account connected
                    </p>
                    <p className="text-sm text-slate-500">
                      Account ID: {settings?.connect_account_id?.slice(0, 12)}...
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-soft-gray rounded-xl p-4">
                    <p className="text-sm text-slate-500">Charges</p>
                    <div className="flex items-center gap-2 mt-1">
                      {settings?.charges_enabled ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-amber-500" />
                      )}
                      <span className="font-medium">
                        {settings?.charges_enabled ? 'Enabled' : 'Pending'}
                      </span>
                    </div>
                  </div>
                  <div className="bg-soft-gray rounded-xl p-4">
                    <p className="text-sm text-slate-500">Payouts</p>
                    <div className="flex items-center gap-2 mt-1">
                      {settings?.payouts_enabled ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-amber-500" />
                      )}
                      <span className="font-medium">
                        {settings?.payouts_enabled ? 'Enabled' : 'Pending'}
                      </span>
                    </div>
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={handleOpenDashboard}
                  className="rounded-full"
                  data-testid="open-stripe-dashboard"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open Stripe Dashboard
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-500" />
                <p className="text-slate-600">
                  Your account has restrictions. Please contact support or update your information.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Renter info */}
        {!isOwner && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <CreditCard className="w-5 h-5 text-trust-blue" />
              <h3 className="font-heading font-semibold text-slate-900">
                Payment Methods
              </h3>
            </div>
            <p className="text-slate-600 mb-4">
              Payments are processed securely through Stripe. You can pay using credit/debit 
              cards when confirming a booking. We don't store your card details.
            </p>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Shield className="w-4 h-4" />
              <span>Your payments are protected by Stripe's secure checkout</span>
            </div>
          </div>
        )}

        {/* Security Info */}
        <div className="bg-slate-900 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-5 h-5 text-trust-blue" />
            <h3 className="font-heading font-semibold">
              Security & Privacy
            </h3>
          </div>
          <p className="text-slate-300 text-sm mb-4">
            Stay Pair Homes never stores your full card numbers or bank account details. All payment 
            processing is handled securely by Stripe, a PCI-compliant payment processor. We 
            only store non-sensitive identifiers to track your transactions.
          </p>
          <div className="flex items-center gap-4">
            <img 
              src="https://cdn.brandfolder.io/KGT2DTA4/at/8vbr8k4mr5xjwk4hxq4t9vs/Stripe_Logo_Revised_2016.svg" 
              alt="Stripe" 
              className="h-8 opacity-80"
            />
            <span className="text-xs text-slate-400">Powered by Stripe</span>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PaymentSettingsPage;
